import {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
  type ChangeEvent,
  type KeyboardEvent,
} from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { Check, MessageSquare, Send } from 'lucide-react';
import { Drawer, useToast } from '@components/base/feedback';
import { Avatar, Badge, Icon, Text, Tooltip } from '@components/base/display';
import { Button } from '@components/base/actions';
import { HStack, VStack } from '@components/base/layout';
import { Spinner } from '@components/base/feedback';
import { useClaimFlowAuthStore } from '../auth/store';
import { commentsService, type VeosComment, type VeosPlanificateur } from '../api/commentsService';
import { useCommentsPanelStore } from './commentsPanelStore';

interface CommentsPanelProps {
  ses: string;
  idSin: string;
  ncbtSin: string;
}

const MENTION_REGEX = /@([\w-]*)$/;

// Matches a posted/returned mention wrapped as `@[Name]`. Distinct from
// MENTION_REGEX above, which drives the `@query` autocomplete trigger.
const MENTION_HIGHLIGHT_REGEX = /@\[([^\]]+)\]/g;

/**
 * Splits `text` on `@[Name]` segments and renders each mention in the
 * design-system `info` blue. The literal brackets are stripped for
 * readability — `@[Pierre Stanislas]` renders as a highlighted `@Pierre
 * Stanislas`. Plain text is returned untouched.
 */
function renderWithMentions(text: string): React.ReactNode {
  const parts: React.ReactNode[] = [];
  let lastIndex = 0;
  let key = 0;
  // Reset lastIndex — the regex is module-scoped and stateful with the /g flag.
  MENTION_HIGHLIGHT_REGEX.lastIndex = 0;
  let match = MENTION_HIGHLIGHT_REGEX.exec(text);
  while (match !== null) {
    if (match.index > lastIndex) {
      parts.push(text.slice(lastIndex, match.index));
    }
    parts.push(
      <span
        key={`mention-${key++}`}
        data-testid="mention-highlight"
        className="text-info-700 font-medium bg-info-50 rounded px-1"
      >
        {`@${match[1]}`}
      </span>,
    );
    lastIndex = match.index + match[0].length;
    match = MENTION_HIGHLIGHT_REGEX.exec(text);
  }
  if (lastIndex < text.length) {
    parts.push(text.slice(lastIndex));
  }
  return parts;
}

function formatDateGroup(iso: string): string {
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return iso;
  return d.toLocaleDateString('fr-FR', {
    day: 'numeric',
    month: 'long',
    year: 'numeric',
  });
}

function formatTime(iso: string): string {
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return '';
  const hh = String(d.getHours()).padStart(2, '0');
  const mm = String(d.getMinutes()).padStart(2, '0');
  return `${hh}:${mm}`;
}

function groupByDate(comments: VeosComment[]): Array<{ date: string; items: VeosComment[] }> {
  const groups = new Map<string, VeosComment[]>();
  for (const c of comments) {
    const key = formatDateGroup(c.DTCRE_US);
    const existing = groups.get(key);
    if (existing) {
      existing.push(c);
    } else {
      groups.set(key, [c]);
    }
  }
  return Array.from(groups.entries()).map(([date, items]) => ({ date, items }));
}

interface CommentRowProps {
  comment: VeosComment;
  onMarkRead: (idEvent: number) => void;
  markingId: number | null;
}

function CommentRow({ comment, onMarkRead, markingId }: CommentRowProps) {
  const isUnread = comment.NONLU === 1;
  const time = formatTime(comment.DTCRE_US);
  const canMarkRead = isUnread && typeof comment.ID_EVENT === 'number';
  const isMarking = markingId === comment.ID_EVENT;
  return (
    <div
      className={[
        'group flex gap-3 p-3 rounded-md',
        isUnread ? 'bg-error-50 border-l-4 border-error-500' : '',
      ].filter(Boolean).join(' ')}
    >
      <Avatar name={comment.AUTEUR} size="sm" />
      <div className="flex-1 min-w-0">
        <HStack gap={2} align="center" wrap>
          <Text as="span" weight="semibold" size="sm">
            {comment.AUTEUR}
          </Text>
          {time && (
            <Text as="span" size="xs" color="muted">
              {time}
            </Text>
          )}
          {isUnread && (
            <span
              className="text-[10px] font-bold uppercase tracking-wide text-error-700"
              data-testid="non-lu-badge"
            >
              NON LU
            </span>
          )}
        </HStack>
        <Text size="sm" className="mt-1 whitespace-pre-wrap break-words">
          {renderWithMentions(comment.LIGN_NSIN)}
        </Text>
      </div>
      {canMarkRead && comment.ID_EVENT !== undefined && (
        <Tooltip content="Marquer comme lu / traité">
          <button
            type="button"
            aria-label="Marquer comme lu / traité"
            disabled={isMarking}
            onClick={() => onMarkRead(comment.ID_EVENT as number)}
            className={[
              'self-start shrink-0 inline-flex items-center justify-center',
              'p-1 rounded-md text-secondary-500 transition',
              'group-hover:opacity-100 focus-visible:opacity-100',
              'hover:bg-primary-700 hover:text-white',
              'focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-success-700',
              isMarking ? 'opacity-100' : 'opacity-0',
            ].join(' ')}
          >
            {isMarking ? (
              <Spinner size="xs" />
            ) : (
              <Check className="w-4 h-4" />
            )}
          </button>
        </Tooltip>
      )}
    </div>
  );
}

interface MentionMatch {
  query: string;
  start: number;
  end: number;
}

function getMentionMatch(
  value: string,
  caret: number,
): MentionMatch | null {
  const upToCaret = value.slice(0, caret);
  const match = MENTION_REGEX.exec(upToCaret);
  if (!match) return null;
  const start = upToCaret.length - match[0].length;
  return { query: match[1] ?? '', start, end: caret };
}

export function CommentsPanel({ ses, idSin, ncbtSin }: Readonly<CommentsPanelProps>) {
  const open = useCommentsPanelStore((s) => s.open);
  const setOpen = useCommentsPanelStore((s) => s.setOpen);
  const cdUtil = useClaimFlowAuthStore((s) => s.cdUtil);
  const queryClient = useQueryClient();
  const { toast } = useToast();

  const textareaRef = useRef<HTMLTextAreaElement>(null);
  const bottomRef = useRef<HTMLDivElement>(null);
  const [text, setText] = useState('');
  const [mentions, setMentions] = useState<number[]>([]);
  const [mention, setMention] = useState<MentionMatch | null>(null);
  const [planificateurs, setPlanificateurs] = useState<VeosPlanificateur[]>([]);
  const [activeMentionIndex, setActiveMentionIndex] = useState(0);

  const { data: comments = [], isLoading } = useQuery({
    queryKey: ['claimflow', 'comments', idSin],
    queryFn: () => commentsService.fetchComments(ses, idSin),
    enabled: open && Boolean(ses && idSin),
  });

  useEffect(() => {
    if (!mention) {
      setPlanificateurs([]);
      return;
    }
    const handle = setTimeout(() => {
      commentsService
        .searchPlanificateurs(ses, mention.query)
        .then(setPlanificateurs)
        .catch(() => setPlanificateurs([]));
    }, 200);
    return () => clearTimeout(handle);
  }, [mention, ses]);

  useEffect(() => {
    setActiveMentionIndex(0);
  }, [planificateurs]);

  // Scroll to the newest comment whenever the list grows or the panel opens.
  // Uses requestAnimationFrame so the scroll happens after the refetch's
  // render commits — without it the ref points at the pre-update DOM.
  useEffect(() => {
    if (!open) return;
    const raf = requestAnimationFrame(() => {
      bottomRef.current?.scrollIntoView?.({ block: 'end' });
    });
    return () => cancelAnimationFrame(raf);
  }, [open, comments.length]);

  const markReadMutation = useMutation({
    mutationFn: async (idEvent: number) => {
      await commentsService.markCommentRead(idEvent);
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['claimflow', 'comments', idSin] });
      queryClient.invalidateQueries({ queryKey: ['claimflow', 'comments-counter', idSin] });
    },
    onError: (err) => {
      toast({
        variant: 'error',
        title: 'Échec du marquage',
        message: err instanceof Error ? err.message : 'Erreur inconnue.',
      });
    },
  });

  const sendMutation = useMutation({
    mutationFn: async () => {
      const trimmed = text.trim();
      if (!trimmed) return;
      if (!cdUtil) {
        throw new Error(
          "Aucun utilisateur ERP n'est associé à la session active — bascule sur la session via le sélecteur en haut, puis réessaye.",
        );
      }
      await commentsService.postCommentWithMentions({
        ses,
        idSin,
        ncbtSin,
        comment: trimmed,
        creator: cdUtil,
        mentions,
      });
    },
    onSuccess: () => {
      setText('');
      setMentions([]);
      setMention(null);
      queryClient.invalidateQueries({ queryKey: ['claimflow', 'comments', idSin] });
      queryClient.invalidateQueries({ queryKey: ['claimflow', 'comments-counter', idSin] });
    },
    onError: (err) => {
      toast({
        variant: 'error',
        title: "Échec de l'envoi du commentaire",
        message: err instanceof Error ? err.message : 'Erreur inconnue.',
      });
    },
  });

  const handleTextChange = useCallback((e: ChangeEvent<HTMLTextAreaElement>) => {
    const value = e.target.value;
    setText(value);
    const caret = e.target.selectionStart ?? value.length;
    setMention(getMentionMatch(value, caret));
  }, []);

  const insertMention = useCallback(
    (planif: VeosPlanificateur) => {
      if (!mention) return;
      const before = text.slice(0, mention.start);
      const after = text.slice(mention.end);
      const replacement = `@${planif.NOM} `;
      const next = before + replacement + after;
      setText(next);
      setMentions((prev) => (prev.includes(planif.ID_PLANIF) ? prev : [...prev, planif.ID_PLANIF]));
      setMention(null);
      setPlanificateurs([]);
      const newCaret = (before + replacement).length;
      requestAnimationFrame(() => {
        const el = textareaRef.current;
        if (el) {
          el.focus();
          el.setSelectionRange(newCaret, newCaret);
        }
      });
    },
    [mention, text],
  );

  const handleKeyDown = useCallback(
    (e: KeyboardEvent<HTMLTextAreaElement>) => {
      if (mention && planificateurs.length > 0) {
        if (e.key === 'ArrowDown') {
          e.preventDefault();
          setActiveMentionIndex((i) => (i + 1) % planificateurs.length);
          return;
        }
        if (e.key === 'ArrowUp') {
          e.preventDefault();
          setActiveMentionIndex((i) => (i - 1 + planificateurs.length) % planificateurs.length);
          return;
        }
        if (e.key === 'Enter' && !e.shiftKey) {
          const candidate = planificateurs[activeMentionIndex];
          if (candidate) {
            e.preventDefault();
            insertMention(candidate);
            return;
          }
        }
        if (e.key === 'Escape') {
          e.preventDefault();
          setMention(null);
          setPlanificateurs([]);
          return;
        }
      }
      if (e.key === 'Enter' && (e.metaKey || e.ctrlKey)) {
        e.preventDefault();
        if (text.trim()) sendMutation.mutate();
      }
    },
    [mention, planificateurs, activeMentionIndex, insertMention, sendMutation, text],
  );

  const groups = useMemo(() => groupByDate(comments), [comments]);
  const total = comments.length;
  const unread = comments.reduce((acc, c) => acc + (c.NONLU === 1 ? 1 : 0), 0);

  const showMentionList = Boolean(mention) && planificateurs.length > 0;
  const canSend = text.trim().length > 0 && !sendMutation.isPending;

  return (
    <Drawer open={open} onClose={() => setOpen(false)} side="right" width="md">
      <Drawer.Header>
        <HStack gap={3} align="center">
          <Icon icon={MessageSquare} size="sm" color="primary" />
          <Text as="span" weight="semibold" size="base">
            Commentaires
          </Text>
          <Badge variant="default" size="sm">
            {total}
          </Badge>
          {unread > 0 && (
            <Badge variant="error" size="sm" data-testid="header-unread-badge">
              {unread}
            </Badge>
          )}
        </HStack>
      </Drawer.Header>
      <Drawer.Body>
        {isLoading && (
          <div className="flex items-center justify-center py-8">
            <Spinner />
          </div>
        )}
        {!isLoading && comments.length === 0 && (
          <div className="text-center py-8">
            <Text size="sm" color="muted">
              Aucun commentaire pour le moment.
            </Text>
          </div>
        )}
        {!isLoading && groups.length > 0 && (
          <VStack gap={4}>
            {groups.map((group) => (
              <div key={group.date}>
                <div className="flex items-center gap-3 my-2">
                  <div className="flex-1 h-px bg-secondary-200" />
                  <Text as="span" size="xs" color="muted">
                    {group.date}
                  </Text>
                  <div className="flex-1 h-px bg-secondary-200" />
                </div>
                <VStack gap={2}>
                  {group.items.map((c, idx) => (
                    <CommentRow
                      key={`${c.DTCRE_US}-${c.AUTEUR}-${idx}`}
                      comment={c}
                      onMarkRead={(id) => markReadMutation.mutate(id)}
                      markingId={
                        markReadMutation.isPending
                          ? (markReadMutation.variables ?? null)
                          : null
                      }
                    />
                  ))}
                </VStack>
              </div>
            ))}
          </VStack>
        )}
        <div ref={bottomRef} data-testid="comments-bottom" aria-hidden="true" />
      </Drawer.Body>
      <Drawer.Footer className="flex-col items-stretch gap-2 !justify-start relative">
        {showMentionList && (
          <div
            className="absolute bottom-full left-6 right-6 mb-2 bg-white border border-secondary-200 rounded-md shadow-lg max-h-60 overflow-y-auto z-10"
            data-testid="mention-list"
          >
            <div className="px-3 py-2 text-[11px] font-semibold uppercase tracking-wide text-secondary-500 border-b border-secondary-100">
              Interpeller un utilisateur
            </div>
            <ul>
              {planificateurs.map((p, idx) => (
                <li key={p.ID_PLANIF}>
                  <button
                    type="button"
                    onMouseDown={(e) => {
                      e.preventDefault();
                      insertMention(p);
                    }}
                    className={[
                      'w-full flex items-center gap-3 px-3 py-2 text-left',
                      idx === activeMentionIndex
                        ? 'bg-primary-100 text-primary-900'
                        : 'hover:bg-secondary-50',
                    ].join(' ')}
                  >
                    <Avatar name={p.NOM} size="sm" />
                    <Text as="span" size="sm" weight="medium">
                      {renderWithMentions(p.NOM)}
                    </Text>
                  </button>
                </li>
              ))}
            </ul>
          </div>
        )}
        <div className="flex items-end gap-2 w-full">
          <textarea
            ref={textareaRef}
            value={text}
            onChange={handleTextChange}
            onKeyDown={handleKeyDown}
            placeholder="Écrire un commentaire... (tapez @ pour interpeller)"
            rows={2}
            className="flex-1 resize-none rounded-md border border-secondary-300 px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-primary-200 focus:border-primary-500"
            aria-label="Écrire un commentaire"
          />
          <Button
            variant="primary"
            onClick={() => sendMutation.mutate()}
            disabled={!canSend}
            loading={sendMutation.isPending}
            leftIcon={<Send className="w-4 h-4" />}
            aria-label="Envoyer le commentaire"
          />
        </div>
      </Drawer.Footer>
    </Drawer>
  );
}
